// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — what a demesne is worth. Kept in its own file with no dependency on rules.ts so the
// mechanism audit can say honestly that it re-derives scoring from the cells rather than importing
// the thing it is checking.
//
// REGION POINTS = CELLS × CORONETS, and that one formula is the entire strategy. A region of eight
// Wold with no coronets scores ZERO; a single Coomb cell with three scores three; six Pinewood with
// two scores twelve. It is why the same doublet is worth eight points to the player with three
// adjacent Orchard cells and two to everyone else, which is where denial lives.
//
// The Motte is in NO region and scores nothing. It is wild for adjacency only — it lets you start,
// it does not let you merge two regions of different land through the middle of the board.
//
// WHOLEFOLD IS GRADED, NOT A CLIFF: max(0, 10 − 2×empty). One discard costs four points plus the
// region value you never built, rather than a flat twelve. That removes the death spiral (a bad draw
// early used to compound into a wrecked game) while keeping a full demesne worth the maximum, so
// "tidy" survives as the fantasy.

import { TERRAIN_COUNT } from './deck';

export const EMPTY = -1;
export const MOTTE = -2;

export interface Region {
  terrain: number;
  cells: number;
  coronets: number;
  points: number;
}

export interface Tally {
  regions: Region[];
  /** Σ cells × coronets. */
  base: number;
  wholefold: number;
  sixfold: number;
  holdfast: number;
  total: number;
  /** Coronets actually placed — the first tie-break. */
  coronets: number;
  /** Cells of the largest single region — the second. */
  largest: number;
  /** Placeable cells still empty — the third, and what Wholefold is graded on. */
  empty: number;
  /** Distinct terrains present, for the Sixfold. */
  terrains: number;
}

export interface Bonuses {
  wholefold: boolean;
  holdfast: boolean;
}

/**
 * Score one demesne. `terr` holds a terrain id, EMPTY or MOTTE per cell; `cor` the coronets on it.
 *
 * The flood fill is an explicit stack rather than recursion — a 7×7 region can be 48 cells deep and
 * this runs a few hundred thousand times inside the balance sim.
 */
export function scoreDemesne(terr: Int8Array, cor: Int8Array, side: number, bonuses: Bonuses): Tally {
  const n = side * side;
  const seen = new Uint8Array(n);
  const regions: Region[] = [];
  const stack: number[] = [];
  const present = new Uint8Array(TERRAIN_COUNT);
  let base = 0;
  let coronets = 0;
  let largest = 0;
  let empty = 0;

  for (let i = 0; i < n; i++) {
    if (terr[i] === EMPTY) empty++;
    else if (terr[i] >= 0) {
      present[terr[i]] = 1;
      coronets += cor[i];
    }
  }

  for (let i = 0; i < n; i++) {
    if (seen[i] || terr[i] < 0) continue;
    const t = terr[i];
    let cells = 0;
    let crowns = 0;
    seen[i] = 1;
    stack.length = 0;
    stack.push(i);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      cells++;
      crowns += cor[c];
      const row = (c / side) | 0;
      const col = c % side;
      if (col > 0 && !seen[c - 1] && terr[c - 1] === t) {
        seen[c - 1] = 1;
        stack.push(c - 1);
      }
      if (col < side - 1 && !seen[c + 1] && terr[c + 1] === t) {
        seen[c + 1] = 1;
        stack.push(c + 1);
      }
      if (row > 0 && !seen[c - side] && terr[c - side] === t) {
        seen[c - side] = 1;
        stack.push(c - side);
      }
      if (row < side - 1 && !seen[c + side] && terr[c + side] === t) {
        seen[c + side] = 1;
        stack.push(c + side);
      }
    }
    const points = cells * crowns;
    base += points;
    if (cells > largest) largest = cells;
    regions.push({ terrain: t, cells, coronets: crowns, points });
  }

  regions.sort((a, b) => b.points - a.points || b.cells - a.cells || a.terrain - b.terrain);

  let terrains = 0;
  for (let t = 0; t < TERRAIN_COUNT; t++) terrains += present[t];

  const wholefold = bonuses.wholefold ? Math.max(0, 10 - 2 * empty) : 0;
  const sixfold = terrains === TERRAIN_COUNT ? 5 : 0;
  let holdfast = 0;
  if (bonuses.holdfast) for (const r of regions) if (r.cells >= 6) holdfast += 3;

  return {
    regions,
    base,
    wholefold,
    sixfold,
    holdfast,
    total: base + wholefold + sixfold + holdfast,
    coronets,
    largest,
    empty,
    terrains,
  };
}

/**
 * Rank seats by the tie-break chain: total, then coronets placed, then largest region, then fewest
 * empty cells — and if all four match, the tie STANDS and both are winners. Never break by seat
 * index: a seat coin-flip is exactly the kind of invisible unfairness the balance sim exists to
 * catch, and here it would be unfairness we put in on purpose.
 */
export function compareTally(a: Tally, b: Tally): number {
  return b.total - a.total || b.coronets - a.coronets || b.largest - a.largest || a.empty - b.empty;
}

/** Indices of every seat that wins, which may be more than one. */
export function winnersOf(tallies: readonly Tally[]): number[] {
  let best = 0;
  for (let i = 1; i < tallies.length; i++) if (compareTally(tallies[i], tallies[best]) < 0) best = i;
  const out: number[] = [];
  for (let i = 0; i < tallies.length; i++) if (compareTally(tallies[i], tallies[best]) === 0) out.push(i);
  return out;
}
